import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmploymentAgreementDetailsDto } from '../../../models/dtos/employment-agreement-details-dto';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { Permission } from 'src/app/common/enums';
import { AnnexListWithDetailsResponse } from 'src/app/models/dtos/annexes/annex-list-with-details-dto';
import { Router } from '@angular/router';
import { AgreementHistoryService } from '../../services/agreement-history.service';
import { PropertyHistoryEntry } from 'src/app/shared/components/property-with-history/models/property-history-entry';
import { WageHistoryEntry } from 'src/app/shared/components/wages-with-history/models/wages-with-history-entry';
import { NewContractDurationUnit } from '../../../models/enums/new-contract-duration-unit.enum';
import { WorkerService } from 'src/app/data/worker.service';

@Component({
  selector: 'app-employment-agreement-details-shared',
  templateUrl: './employment-agreement-details-shared.component.html',
  styleUrls: ['./employment-agreement-details-shared.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EmploymentAgreementDetailsSharedComponent implements OnInit {
  private authService = inject(AuthService);
  private workerService = inject(WorkerService);
  readonly contractDurationUnit = NewContractDurationUnit;

  @Input() agreement!: EmploymentAgreementDetailsDto;
  @Input() annexesData: AnnexListWithDetailsResponse | null = null;
  showBankAccountNumber$!: Observable<boolean>;

  private router = inject(Router);
  private historyService = inject(AgreementHistoryService);

  ngOnInit(): void {
    this.showBankAccountNumber$ = this.workerService
      .getBankAccountNumberConfiguration(this.agreement.WorkerId)
      .pipe(map(configuration => !configuration.IsBankAccountHidden));
  }

  get hasAssignOrderItemPermission(): boolean {
    return this.authService.hasPermission(Permission.AssignOrderToAgreement);
  }

  get showHistory(): boolean {
    return !!this.annexesData && this.annexesData.Annexes.length > 0;
  }

  get jobTitleHistory(): PropertyHistoryEntry[] {
    if (!this.annexesData) return [];
    return this.historyService.buildPropertyHistory('jobTitle', this.annexesData.WorkerAgreement, this.annexesData.Annexes);
  }

  get organizationalUnitHistory(): PropertyHistoryEntry[] {
    if (!this.annexesData) return [];
    return this.historyService.buildPropertyHistory('organizationalUnit', this.annexesData.WorkerAgreement, this.annexesData.Annexes);
  }

  get workplaceHistory(): PropertyHistoryEntry[] {
    if (!this.annexesData) return [];
    return this.historyService.buildPropertyHistory('workplace', this.annexesData.WorkerAgreement, this.annexesData.Annexes);
  }

  get wagesHistory(): WageHistoryEntry[] {
    if (!this.annexesData) return [];
    return this.historyService.buildWagesHistory(this.annexesData.WorkerAgreement, this.annexesData.Annexes);
  }

  get shouldShowPlannedPeriodRow(): boolean {
    return this.agreement?.IsPlannedPeriodSectionRequired || this.hasPlannedPeriodValue;
  }

  get hasPlannedPeriodValue(): boolean {
    return this.agreement?.NewContractDurationValue != null && this.agreement?.NewContractDurationUnit != null;
  }

  addWageCount(index: number): string {
    return index === 0 ? '' : ` ${index + 1}`;
  }

  navigateToAnnex(annexId: number): void {
    this.router.navigate([
      '/workers',
      this.agreement.WorkerId,
      'employmentType',
      this.agreement.EmploymentTypeId,
      'agreements',
      this.agreement.AgreementId,
      'annexes',
      annexId,
      'details',
    ]);
  }
}
